import { wilsonLowerBound } from './audience.js';

/**
 * The cascade: a cheap local model decides what is worth asking the expensive
 * one about.
 *
 * The saving only exists because a clean local ruling skips the remote call,
 * which means the local model is APPROVING things. A 8GB model should not get
 * the last word on whether a slur airs, so three rules keep it honest:
 *
 *   1. A local approval is short-lived. It buys one cycle, not standing consent.
 *   2. A fixed sample of clean local rulings is escalated anyway, so the local
 *      model's false-negative rate against the real judge is MEASURED rather
 *      than hoped for.
 *   3. After the judge rejects a feed, that feed is on probation: every ruling
 *      escalates until it has been clean at the judge for a while. A weak model
 *      must not be able to quietly undo a real rejection.
 *
 * A local model that errors escalates. It never passes by default.
 */

export type Escalation =
  /**
   * `submitted` is an ENTRY POINT rather than a finding about this item, which
   * is why it is named separately from the other four. The audit accounting
   * below reads these reasons, and folding a submission into `audit` would count
   * an unconditional escalation as a sample of a tier that was never trusted.
   */
  | { escalate: true; why: EscalationReason }
  | { escalate: false; why: 'local-clean' };

/**
 * Why a review reached the judge, and it lives here ALONE.
 *
 * `observation.ts` and `corpus.ts` each declared this union themselves until
 * 2026-09-03, which is two copies of one decision in a repository that has been
 * bitten by that repeatedly. Adding `submitted` to `decideEscalation` reddened
 * both, which is the compiler doing what the duplication had been preventing.
 */
export type EscalationReason =
  'flagged' | 'probation' | 'audit' | 'filter-error' | 'submitted';

export interface CascadeConfig {
  /** Fraction of clean local rulings sent to the judge anyway, 0..1. */
  auditRate: number;
  /** Cycles a feed stays on probation after the judge rejects it. */
  probationCycles: number;
  /** How long a locally-approved verdict is valid, in ms. Shorter than remote. */
  localTtlMs: number;
}

export const DEFAULT_CASCADE: CascadeConfig = {
  // 1 in 8. Enough to build a usable sample over a game without eating the
  // saving the cascade exists to produce.
  auditRate: 0.125,
  probationCycles: 6,
  // Shorter than the judge's TTL, but still longer than a full sample-to-verdict
  // cycle. See the note on verdictTtlMs: a TTL below the sampling window makes
  // every verdict dead on arrival.
  //
  // `REVISED` 2026-08-29, wave 2: raised from 30s because the two barriers moved
  // the floor. `shared/deadlines.ts` computes the minimum speech verdict age as
  // 8000 span + 1500 align + 8000 review + 1500 select + 10000 refresh = 29,000ms,
  // and 30s over that is a 1s margin, which is not a margin. Still 10s BELOW the
  // remote TTL, so the invariant that a local approval expires faster than a
  // judged one holds, and `ReviewLoop.start()` warns rather than assumes.
  // 35s -> 53s on 2026-08-30, and neither number was chosen: both are the floor
  // plus two seconds. Issue 016 took `windowMs` and `reviewDeadlineMs` to 20s,
  // and `minVerdictAgeMs` carries both, so the speech floor went 33s -> 51s. A
  // TTL below the floor makes every verdict dead on arrival: nothing is ever
  // fresh, nothing is ever eligible, and it presents as a gate bug.
  localTtlMs: 53_000,
};

export interface CascadeState {
  /** Counts down after a remote rejection. Above zero means force escalation. */
  probationRemaining: number;
  /** Cycles seen, used to make audit sampling deterministic rather than random. */
  cycle: number;
}

/**
 * Deterministic audit sampling, keyed on a counter rather than Math.random().
 * A random sample is fine statistically and miserable operationally: you cannot
 * reproduce a session, and you cannot tell "we got unlucky" from "the sampler
 * is broken".
 */
/**
 * Which entry point this review is for, because they can afford different things.
 *
 * `docs/plans/2026-09-03-submission-as-a-second-entry-point.md` §7.3. A live
 * review is racing the broadcast hold; a submitted one is not, so it can carry
 * the judge on every item where the live path can only afford it on a fifth.
 */
export type ReviewEntry = 'live' | 'submitted';

export function decideEscalation(
  filterApproved: boolean | null,
  state: CascadeState,
  cfg: CascadeConfig,
  entry: ReviewEntry = 'live',
): Escalation {
  if (filterApproved === null) return { escalate: true, why: 'filter-error' };
  if (!filterApproved) return { escalate: true, why: 'flagged' };
  /*
   * A SUBMISSION ESCALATES UNCONDITIONALLY, and this is the whole argument for
   * the second entry point rather than a saving recovered.
   *
   * The cascade skips the judge on most reviews, and the audit sample and
   * probation exist to make that skipping defensible. Both exist because live
   * review cannot afford the judge inside a window. A submission is not inside a
   * window.
   *
   * **This is not what `CLAUDE.md` forbids.** That invariant protects a tier
   * that is APPROVING: never remove the audit sample or probation, because they
   * are what make a local approval defensible. Here the local tier approves
   * nothing, so there is no local approval to defend and nothing is removed. The
   * two checks below are not switched off; they have no subject. **Removing them
   * on the LIVE path is still the violation it has always been**, which is why
   * this is an entry-point argument rather than a flag: a flag is how one
   * becomes the other.
   *
   * It sits after `flagged` deliberately. The cheap tier may still REFUSE, and
   * naming that refusal is more informative than naming the entry point.
   */
  if (entry === 'submitted') return { escalate: true, why: 'submitted' };
  if (state.probationRemaining > 0) return { escalate: true, why: 'probation' };
  if (auditDue(state, cfg)) return { escalate: true, why: 'audit' };

  return { escalate: false, why: 'local-clean' };
}

/**
 * Is THIS cycle the one the audit lands on?
 *
 * Exported and consulted in two places, which is the whole of issue 021's fix.
 * The caller that decides whether to escalate is not the only caller that needs
 * this: a cheap SHORTCUT taken before the escalation test is reached will hide
 * the audit from the sample it was configured to take, and the shortcut cannot
 * know that unless it can ask.
 *
 * The counter and this test must be evaluated on the same population or the
 * delivered rate is not the configured one. Measured 2026-08-30: with the
 * visual scene gate returning early on 4 windows in 5, the audit reached 1 in
 * 40 broadcast windows against a configured 1 in 8, and nothing anywhere said
 * so. See `auditCoverage` for the instrument that now would.
 */
export function auditDue(state: CascadeState, cfg: CascadeConfig): boolean {
  const every = Math.max(1, Math.round(1 / cfg.auditRate));
  return state.cycle % every === 0;
}

/**
 * Where a feed's audit counter STARTS, so nine feeds do not all escalate in the
 * same window.
 *
 * Every counter started at zero, so every feed reached its audit cycle
 * together, and an escalation pays both tiers in sequence. `STATUS.md` records
 * the consequence in the words it was observed in: the audit sample "cuts the
 * broadcast to the slate every sixty seconds, on a timer, while every feed is
 * fine", and **it takes the whole broadcast rather than one feed** precisely
 * because the counters are in phase. Issue 016's cost is a spike, and a spike is
 * a scheduling property rather than a speed one.
 *
 * Derived from the publisher's PATH, so it is stable across a reconnect for the
 * same reason `Cascade` keys its state on the path: an offset that changed when
 * a cable was pulled would let a feed skip its audit by reconnecting.
 *
 * **Deterministic, not random**, which is the property `decideEscalation`'s
 * docblock already insists on: a random sample is fine statistically and
 * miserable operationally, because you cannot reproduce a session and cannot
 * tell "we got unlucky" from "the sampler is broken". This is a fixed function
 * of the path, so the same feeds on the same node audit in the same order every
 * time.
 */
export function auditPhase(key: string, cfg: CascadeConfig): number {
  const every = Math.max(1, Math.round(1 / cfg.auditRate));
  // FNV-1a, for no reason except that it is short, stable and has no imports.
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % every;
}

export interface FilterAudit {
  /** Clean local rulings that were escalated anyway, so we could check them. */
  audited: number;
  /** Of those, how many the judge rejected. Each one is a miss that would
   *  have gone to air on the local model's word alone. */
  missed: number;
  /** Point estimate of the miss rate. */
  missRate: number;
  /**
   * Wilson lower bound on the miss rate. The honest number: given this sample,
   * at least this fraction of what the filter waves through is actually bad.
   */
  missRateLowerBound: number;
  /**
   * Audit escalations the judge could not RULE on, so they measure nothing.
   *
   * A judge that failed did not disagree with the filter. Counting an outage as
   * a miss reports the local model as unreliable when the only thing that is
   * unreliable is the connection, and the director's bar said exactly that:
   * "missed 350 of 350 audited, at least 99% with 95% confidence", on a machine
   * where the judge has never ruled once. Measured 2026-08-29.
   *
   * Kept and surfaced rather than silently dropped: an audit sample of zero
   * because the judge is down is a fact a director needs, and it is issue 001
   * stated in a number.
   */
  unmeasured: number;
  /** Remote calls avoided, and the calls actually made. */
  skipped: number;
  escalated: number;
  /**
   * Of everything that reached air on the cheap tier's word, what fraction had
   * a judge's ruling behind it.
   *
   * **The configured rate is a promise and this is the delivery.** `auditRate`
   * says one clean local ruling in eight is checked anyway, and that promise is
   * the whole defence of letting an 8GB model approve anything. Issue 021 is
   * what happens when the two diverge silently: a live run put 67 windows to
   * air on one escalation, a fifth of the configured sample, and every number
   * on every surface was individually true.
   *
   * `null` rather than 0 when nothing has aired on a local ruling, because a
   * coverage of zero and nothing to cover are different claims, and this
   * repository has already shipped "missed 350 of 350" on a node whose judge
   * had never ruled.
   */
  auditCoverage: number | null;
  /**
   * What `auditRate` asked for, travelling WITH what was delivered.
   *
   * A number is only as good as the quantity it is compared against, and a
   * coverage figure alone is unreadable: 0.025 is either a fifth of the sample
   * this node promised or exactly what it promised, depending on a constant the
   * reader does not have. Issue 021 sat unread for a day for that reason, so
   * the pair is reported together and neither is quotable alone.
   */
  auditRateConfigured: number;
  /** Fraction of reviews that never reached the judge. The saving. */
  savingRate: number;
}

export class FilterScorecard {
  private audited = 0;
  private missed = 0;
  private unmeasured = 0;
  private skipped = 0;
  private escalated = 0;

  /**
   * A clean local ruling that we escalated for checking.
   *
   * **Takes the OBSERVATION, not a boolean, and that is the point.** The caller
   * used to pass `judged.approved`, which is `false` for a review that FAILED,
   * so every errored judge was counted as the local filter having missed
   * something. This is the third time this repository has recorded an instrument
   * failure as a judgment: probation armed by an errored verdict (a permanent
   * lock-out), a contaminated review scoring down instead of being excluded, and
   * now this. A boolean cannot carry the difference, so it no longer takes one.
   */
  recordAudit(judged: { approved: boolean; error?: string }): void {
    if (judged.error !== undefined) {
      this.unmeasured++;
      return;
    }
    this.audited++;
    if (!judged.approved) this.missed++;
  }

  recordSkip(): void {
    this.skipped++;
  }

  recordEscalation(): void {
    this.escalated++;
  }

  audit(cfg: CascadeConfig = DEFAULT_CASCADE, z = 1.96): FilterAudit {
    const total = this.skipped + this.escalated;
    /*
     * The denominator is what reached air TRUSTING the cheap tier: every skip,
     * plus the clean rulings we escalated only in order to check them. A
     * `flagged` or `probation` escalation is not in it, because the cheap tier
     * was not trusted on those and they are not a sample of anything.
     *
     * `unmeasured` stays in the numerator: an audit whose judge errored was
     * still TAKEN, and dropping it here would make an outage read as a smaller
     * sample rather than as a failed one, which `unmeasured` exists to prevent.
     */
    const onLocalWord = this.skipped + this.audited + this.unmeasured;
    return {
      auditCoverage: onLocalWord === 0
        ? null
        : (this.audited + this.unmeasured) / onLocalWord,
      auditRateConfigured: cfg.auditRate,
      audited: this.audited,
      missed: this.missed,
      unmeasured: this.unmeasured,
      missRate: this.audited === 0 ? 0 : this.missed / this.audited,
      missRateLowerBound: wilsonLowerBound(this.missed, this.audited, z),
      skipped: this.skipped,
      escalated: this.escalated,
      savingRate: total === 0 ? 0 : this.skipped / total,
    };
  }
}
